/**
 * Stack-to-Pot Ratio (SPR) quiz.
 *
 * SPR = effective stack / pot. It's the simplest number that
 * predicts stack-committal pressure on a postflop street:
 *   - SPR ≤ 3:  commit-or-fold territory (no big folds with strong made hands)
 *   - SPR 4–8:  medium — standard postflop play
 *   - SPR > 10: deep — careful play, implied odds matter
 *
 * The quiz folds two skills into one question:
 *   1. **Effective stack** — `min(hero_stack, villain_stack)`. The
 *      smaller stack is what's actually playable; deeper stacks can't
 *      win money beyond that.
 *   2. **SPR computation** — eff_stack / pot.
 *
 * Procedural questions show hero stack + villain stack + pot. User
 * computes effective stack mentally, then divides by pot. Distractors
 * include common confusions: using hero's stack instead of effective,
 * inverting the ratio, etc.
 *
 * Questions are constructed so the SPR always lands on an integer for
 * clean multiple-choice answers.
 */

/** SPR values we generate questions for. Spread across the meaningful
 *  bands: commit (1–3), medium (4–8), deep (10+). */
import { pickFrom, shuffle } from "@/features/quiz/lib/common";

const CANONICAL_SPRS = [1, 2, 3, 4, 5, 6, 8, 10] as const;

/** Pot sizes (bb) we draw from. Bounded so eff_stack stays realistic
 *  (under 200bb cash). */
const POT_SIZES = [10, 15, 20, 25, 30, 40, 50] as const;

/** Spread between hero and villain stacks so effective stack isn't
 *  always hero's. Variance is intentional — the quiz tests "did you
 *  use the smaller stack?", which is the leak this drill targets. */
const STACK_DELTAS = [0, 5, 10, 15, 20, 25, 40] as const;

export interface SprQuestion {
  pot: number;
  /** Smaller of the two stacks — what the user should compute SPR from. */
  effectiveStack: number;
  heroStack: number;
  villainStack: number;
  answer: number;
  options: number[];
  correctIndex: number;
}

export function generateQuestion(
  rand: () => number = Math.random,
): SprQuestion {
  const pot = pickFrom(POT_SIZES, rand);
  const targetSpr = pickFrom(CANONICAL_SPRS, rand);
  const effectiveStack = pot * targetSpr;

  // Assign which of hero/villain has the effective (smaller) stack
  const heroIsShort = rand() < 0.5;
  const delta = pickFrom(STACK_DELTAS, rand);
  const heroStack = heroIsShort ? effectiveStack : effectiveStack + delta;
  const villainStack = heroIsShort ? effectiveStack + delta : effectiveStack;

  // Distractors capture the canonical confusions:
  //  - hero stack / pot (ignoring effective)
  //  - villain stack / pot (same mistake, other direction)
  //  - pot / eff stack (inverted ratio)
  //  - nearby canonical SPR
  const heroSpr = Math.round(heroStack / pot);
  const villainSpr = Math.round(villainStack / pot);
  const inverted = Math.round((pot / effectiveStack) * 10) / 10; // small number
  const nearbyCanonical = pickFrom(
    CANONICAL_SPRS.filter((s) => s !== targetSpr),
    rand,
  );

  const candidates = new Set<number>([
    targetSpr,
    heroSpr,
    villainSpr,
    inverted,
    nearbyCanonical,
  ]);
  // Backfill if collisions reduced us below 4 options
  let attempts = 0;
  while (candidates.size < 4 && attempts < 20) {
    candidates.add(pickFrom(CANONICAL_SPRS, rand));
    attempts++;
  }
  const options = shuffle([...candidates].slice(0, 4), rand);
  // Ensure the correct answer made it into the trimmed set (it should
  // — it was the first item — but be defensive).
  if (!options.includes(targetSpr)) {
    options[0] = targetSpr;
  }
  return {
    pot,
    effectiveStack,
    heroStack,
    villainStack,
    answer: targetSpr,
    options,
    correctIndex: options.indexOf(targetSpr),
  };
}

/** Human-readable band label for an SPR value — used in the reveal
 *  to teach the practical play implication, not just the math. */
export function sprBand(spr: number): string {
  if (spr <= 3) return "commit-or-fold band (low SPR: strong hands stack off easily)";
  if (spr <= 8) return "standard postflop band (medium SPR)";
  return "deep / careful-play band (high SPR: implied odds matter)";
}
